import { FormEvent, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SiteLayout from '@/components/feature/SiteLayout';
import { useSeo } from '@/hooks/useSeo';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const topicsJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'CollectionPage',
      name: '爆料看料 · 专题分类',
      description: '按主题浏览爆料看料的全部深度拆解内容，涵盖娱乐、行业、品牌、社会与文化五大方向。',
      inLanguage: 'zh-CN',
      url: 'https://cicccqh.cn/topics',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://cicccqh.cn/' },
        { '@type': 'ListItem', position: 2, name: '专题分类', item: 'https://cicccqh.cn/topics' },
      ],
    },
  ],
};

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get('cat') || 'all';
  const query = searchParams.get('q') || '';
  const [keyword, setKeyword] = useState(query);

  useSeo({
    title: '专题分类 - 爆料看料深度拆解内容总览',
    description:
      '浏览爆料看料全部专题分类：娱乐深扒、行业起底、品牌扒皮、社会观察与文化解码，支持关键词检索，快速找到你关心的深度分析内容。',
    keywords: '爆料看料专题,深度拆解分类,行业起底,品牌扒皮,社会观察,文化解码',
    path: '/topics',
    jsonLd: topicsJsonLd,
  });

  const activeCategory = categories.find((c) => c.id === activeCat);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((article) => {
      const matchCat = activeCat === 'all' || article.categoryId === activeCat;
      if (!matchCat) return false;
      if (!q) return true;
      const haystack = [article.title, article.excerpt, article.categoryName, ...article.tags]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [activeCat, query]);

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    const value = keyword.trim();
    if (value) next.set('q', value);
    else next.delete('q');
    setSearchParams(next);
  };

  const selectCat = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'all') next.delete('cat');
    else next.set('cat', id);
    setSearchParams(next);
  };

  return (
    <SiteLayout>
      <div className="bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-14">
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '专题分类' }]} />
          <h1 className="font-heading mt-5 text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
            {activeCategory ? activeCategory.name : '全部专题分类'}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {activeCategory
              ? activeCategory.desc
              : '五大主题方向，共整理出数百篇深度拆解。先用分类缩小范围，再用关键词直达你关心的事件。'}
          </p>

          <form onSubmit={handleSearch} className="mt-7 flex w-full max-w-xl flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索标题、标签或关键词"
                aria-label="搜索深度内容"
                className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              检索
            </button>
          </form>
        </div>
      </div>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
          <div className="flex flex-wrap items-center gap-2 border-b border-background-200/70 pb-5">
            <button
              type="button"
              onClick={() => selectCat('all')}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                activeCat === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              全部
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => selectCat(cat.id)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  activeCat === cat.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            共找到 <strong className="text-foreground-950">{filtered.length}</strong> 篇相关拆解
            {query && (
              <>
                ，关键词「<strong className="text-primary-700">{query}</strong>」
              </>
            )}
          </p>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-500">
                <i className="ri-search-eye-line" />
              </span>
              <h3 className="font-heading mt-4 text-lg font-bold text-foreground-950">暂时没有匹配的拆解</h3>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                换个关键词，或者点击下方按钮回到全部专题。也欢迎把这条件线索提交给我们。
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setKeyword('');
                    setSearchParams(new URLSearchParams());
                  }}
                  className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看全部专题
                </button>
                <a
                  href="#subscribe"
                  className="whitespace-nowrap rounded-md border border-background-300 px-6 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400"
                >
                  提交线索
                </a>
              </div>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}